/**
 * JsonViewer Component
 * Collapsible, formatted JSON display for analytics/EDL data
 */

import { useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronRight, ChevronDown, Copy, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface JsonViewerProps {
  data: unknown;
  title: string;
  defaultOpen?: boolean;
  maxHeight?: string;
  className?: string;
}

export function JsonViewer({
  data,
  title,
  defaultOpen = false,
  maxHeight = "300px",
  className,
}: JsonViewerProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);

  const jsonString = JSON.stringify(data, null, 2);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!data || (typeof data === "object" && Object.keys(data).length === 0)) {
    return (
      <div className={cn("rounded-lg border p-3", className)}>
        <p className="text-sm text-muted-foreground">{title}: No data</p>
      </div>
    );
  }

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn("rounded-lg border", className)}
    >
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50">
          <div className="flex items-center gap-2">
            {isOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <span className="text-sm font-medium">{title}</span>
            <span className="text-xs text-muted-foreground">
              ({typeof data === "object" ? Object.keys(data).length : 1}{" "}
              {typeof data === "object" && Object.keys(data).length !== 1
                ? "keys"
                : "key"}
              )
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="h-7 px-2"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ScrollArea style={{ maxHeight }} className="w-full">
          <pre className="p-3 pt-0 text-xs font-mono overflow-x-auto">
            <code>
              <JsonSyntaxHighlight json={jsonString} />
            </code>
          </pre>
        </ScrollArea>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Syntax highlighting for JSON using Tailwind classes
 */
function JsonSyntaxHighlight({ json }: { json: string }) {
  // Simple regex-based syntax highlighting
  const highlighted = json
    .replace(
      /("(?:[^"\\]|\\.)*")\s*:/g,
      '<span class="text-blue-600 dark:text-blue-400">$1</span>:',
    )
    .replace(
      /:\s*("(?:[^"\\]|\\.)*")/g,
      ': <span class="text-green-600 dark:text-green-400">$1</span>',
    )
    .replace(
      /:\s*(\d+\.?\d*)/g,
      ': <span class="text-amber-600 dark:text-amber-400">$1</span>',
    )
    .replace(
      /:\s*(true|false)/g,
      ': <span class="text-purple-600 dark:text-purple-400">$1</span>',
    )
    .replace(
      /:\s*(null)/g,
      ': <span class="text-red-600 dark:text-red-400">$1</span>',
    );

  return <span dangerouslySetInnerHTML={{ __html: highlighted }} />;
}

/**
 * Compact inline JSON viewer for small data
 */
export function InlineJsonViewer({
  data,
  maxLength = 100,
}: {
  data: unknown;
  maxLength?: number;
}) {
  const jsonString = JSON.stringify(data);

  if (jsonString.length <= maxLength) {
    return (
      <code className="text-xs bg-muted px-1 py-0.5 rounded">{jsonString}</code>
    );
  }

  return (
    <code className="text-xs bg-muted px-1 py-0.5 rounded">
      {jsonString.slice(0, maxLength)}...
    </code>
  );
}
